import { HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ParamMap } from '@angular/router';
import { marker as extractTranslation } from '@colsen1991/ngx-translate-extract-marker';
import { TranslateService } from '@ngx-translate/core';
import { AppService } from '@nk/access-block';
import {
  AppFeatureKey,
  AppRoleKey,
  COMPANY_FLAG_FEATURES,
  COMPANY_FLAG_ROLES,
  COUNTRY_FEATURES,
  CompanyBatchEntity,
  CompanyCollateralTypeEntity,
  CompanyFlagTypeEntity,
  GeoCountryEntity,
  GeoMunicipalityEntity,
  GeoSubdivisionEntity,
  NACE_CODE_SCHEMAS,
  NaceCodeEntity,
  SimpleCollection,
  TransientFileMetaEntity,
} from '@nk/data-entities-lib';
import {
  Dict,
  arrayDistinct,
  arrayToDict,
  arrayUnique,
  decodeBaseNMaskToIds,
  encodeIdsToBaseNMask,
  includes,
} from '@nk/utils-lib';
import { Observable, ReplaySubject, combineLatest, forkJoin, of } from 'rxjs';
import { catchError, filter, first, map, startWith, switchMap } from 'rxjs/operators';
import { DEFAULT_SELECTED_COLUMNS } from './company-browser-config';
import { CompanyBrowserController } from './company-browser-controller';
import {
  AccountsYearMode,
  CompanyBrowserCompositeEntity,
  CompanyBrowserExtendedCompositeCollection,
  CompanyBrowserFilter,
  CompanyBrowserFilterKey,
  CompanyBrowserOrdering,
  OPERATORS,
  TabularFileFormat,
  fromAbbr,
} from './company-browser-entities';
import { CompanyFlagService } from './company-flag.service';
import { GeoService } from './geo.service';
import { NaceCodeService } from './nace-code.service';

import { ApplicationApiClientAccess } from '@nk/access-block';
import { isNkHttpError } from '@nk/api-client-lib';
import { MEMBERSHIP_GEO_COUNTRY_API_SERVICE_TOKEN, MembershipGeoCountryApiService } from '@nk/api-lib';
import { getFirstAndLastNaceCodeHierarchyLevel } from '@nk/company/browser-view/components/filters/browser-filter-industry/browser-filter-industry.utils';
import { CollateralService } from '@nk/core/services/collateral.service';
import { DEFAULT_LANGUAGE_CODE } from '@nk/localization-lib';
import { NkToastrService } from '@nk/ui-lib';

@Injectable()
export class CompanyBrowserService {
  private _companyFlagTypeDict: Dict<CompanyFlagTypeEntity>;
  private _municipalityDict: Dict<GeoMunicipalityEntity>;
  private _subdivisionDict: Dict<GeoSubdivisionEntity>;
  private _geoCountryDict: Dict<GeoCountryEntity>;
  private _collateralTypeDict: Dict<CompanyCollateralTypeEntity>;

  private _naceCodes: NaceCodeEntity[];
  private _naceCodeDict: Dict<NaceCodeEntity>;
  private _naceCodesParentDict: Dict<NaceCodeEntity[]>;
  private _allNaceCodesByIdDict: Dict<NaceCodeEntity>;

  private _naceCodesFirstLevel: number;
  private _naceCodesLastLevel: number;

  private _initialDataLoadingSubject = new ReplaySubject<boolean>(1);
  public initialDataLoading = this._initialDataLoadingSubject.asObservable();

  constructor(
    private _applicationApiAccess: ApplicationApiClientAccess,
    private _appService: AppService,
    private _companyFlagService: CompanyFlagService,
    private _geoService: GeoService,
    private _collateralService: CollateralService,
    private _naceCodeService: NaceCodeService,
    @Inject(MEMBERSHIP_GEO_COUNTRY_API_SERVICE_TOKEN)
    private _membershipGeoCountryService: MembershipGeoCountryApiService,
    private _translateService: TranslateService,
    private _toastr: NkToastrService,
  ) {
    this._initialDataLoadingSubject.next(true);
    forkJoin([
      this._companyFlagService.companyFlagTypeListCache.get().pipe(first()),
      this._geoService.geoMunicipalityListCache.getStableCache().pipe(first()),
      this._geoService.geoSubdivisionListCache.getStableCache().pipe(first()),
      this._geoService.geoCountryListCache.getStableCache().pipe(first()),
      this._collateralService.companyCollateralTypeListCache.getStableCache().pipe(first()),
      this._naceCodeService.naceCodeListCache.getStableCache(null, NACE_CODE_SCHEMAS).pipe(first()), // Todo: might have to get all if url contains nace code from outside preference
    ]).subscribe(([companyFlagTypeList, municipalities, subdivisions, geoCountry, collateralTypes, naceCodes]) => {
      this._companyFlagTypeDict = arrayToDict(companyFlagTypeList, (cf) => cf.uuid);
      this._municipalityDict = arrayToDict(municipalities, (m) => m.uuid);
      this._subdivisionDict = arrayToDict(subdivisions, (s) => s.uuid);
      this._geoCountryDict = arrayToDict(geoCountry, (c) => c.uuid);
      this._collateralTypeDict = arrayToDict(collateralTypes, (ct) => ct.uuid);
      this._naceCodeDict = arrayToDict(naceCodes, (ct) => ct.uuid);
      this._naceCodes = naceCodes;
      this._naceCodesParentDict = this._naceCodes.reduce((res: { [naceCodeUuid: string]: NaceCodeEntity[] }, i) => {
        if (!res[i.uuid]) {
          res[i.uuid] = this._naceCodes.filter((k) => k.parent_nace_code_uuid === i.uuid);
        }
        return res;
      }, {});

      this._allNaceCodesByIdDict = arrayToDict(this._naceCodes, (nc) => nc.nace_code_id.toString());
      const naceCodeLevels = getFirstAndLastNaceCodeHierarchyLevel(naceCodes);
      this._naceCodesFirstLevel = naceCodeLevels.firstLevel;
      this._naceCodesLastLevel = naceCodeLevels.lastLevel;

      this._initialDataLoadingSubject.next(false);
    });
  }

  public static getActiveSelectionGroups(
    companyBrowser: CompanyBrowserController,
    accountsYearMode: AccountsYearMode,
  ): string[] {
    let activeAccountsSelectionGroups: string[];
    if (accountsYearMode === AccountsYearMode.Multiple) {
      activeAccountsSelectionGroups = arrayUnique(
        companyBrowser.allColumns
          .filter((col) => col.fieldGroup.startsWith('accounts_'))
          .filter((col) => col.fieldGroup.endsWith('_history'))
          .map((col) => col.selectionGroup),
      );
    } else if (companyBrowser.filterAccountingYear) {
      activeAccountsSelectionGroups = arrayUnique(
        companyBrowser.allColumns
          .filter((col) => col.fieldGroup.startsWith('accounts_'))
          .filter((col) => !col.fieldGroup.endsWith('_history'))
          .filter((col) => !col.fieldGroup.endsWith('_latest'))
          .map((col) => col.selectionGroup),
      );
    } else {
      activeAccountsSelectionGroups = arrayUnique(
        companyBrowser.allColumns
          .filter((col) => col.fieldGroup.startsWith('accounts_'))
          .filter((col) => col.fieldGroup.endsWith('_latest'))
          .map((col) => col.selectionGroup),
      );
    }

    let activeCompanyShareIssueEnterpriseValueGroup: string;
    if (accountsYearMode === AccountsYearMode.Multiple) {
      activeCompanyShareIssueEnterpriseValueGroup = 'company_share_issue_enterprise_value_history';
    } else if (companyBrowser.filterAccountingYear) {
      activeCompanyShareIssueEnterpriseValueGroup = 'company_share_issue_enterprise_value';
    } else {
      activeCompanyShareIssueEnterpriseValueGroup = 'company_share_issue_enterprise_value_latest';
    }

    const groups = [
      ...arrayUnique(
        companyBrowser.allColumns
          .filter((col) => !col.fieldGroup.startsWith('accounts_'))
          .filter((col) => col.fieldGroup !== 'company_share_issue_enterprise_value')
          .filter((col) => !col.fieldGroup.startsWith('company_share_issue_enterprise_value_'))
          .map((col) => col.selectionGroup),
      ),
      ...activeAccountsSelectionGroups,
      activeCompanyShareIssueEnterpriseValueGroup,
    ];
    return groups;
  }

  public static switchSelectedAccountsColumns(
    companyBrowser: CompanyBrowserController,
    accountsYear: number,
    accountsYearMode: AccountsYearMode,
  ): void {
    let postfix = '';
    if (accountsYearMode === AccountsYearMode.Single) {
      if (accountsYear) {
        postfix = '';
      } else {
        postfix = '_latest';
      }
    } else if (accountsYearMode === AccountsYearMode.Multiple) {
      postfix = '_history';
    }

    const toBeDeactivated = companyBrowser.selectedColumns
      .filter(
        (col) =>
          col.fieldGroup.startsWith('accounts_') || col.fieldGroup.startsWith('company_share_issue_enterprise_value'),
      )
      .map((col) => col.fieldKey);

    const toBeActivated = companyBrowser.selectedColumns
      .filter(
        (col) =>
          col.fieldGroup.startsWith('accounts_') || col.fieldGroup.startsWith('company_share_issue_enterprise_value'),
      )
      .map((col) => {
        const fieldGroup = col.fieldGroup.replace('_latest', '').replace('_history', '');
        return `${fieldGroup}${postfix}.${col.fieldName}`;
      });

    companyBrowser.deactivateColumns(toBeDeactivated);
    companyBrowser.activateColumns(toBeActivated);
  }

  private static _base62MaskToNaceCodes(
    allNaceCodesByIdDict: Dict<NaceCodeEntity>,
    base62Mask: string,
  ): NaceCodeEntity[] {
    const naceCodeIds = decodeBaseNMaskToIds(base62Mask);
    return arrayDistinct(naceCodeIds.map((id) => allNaceCodesByIdDict[id]));
  }

  public static setAccountingYearRange(
    companyBrowser: CompanyBrowserController,
    accountsYearMode: AccountsYearMode,
  ): void {
    if (accountsYearMode === AccountsYearMode.Single) {
      companyBrowser.toAccountingYear = null;
      companyBrowser.fromAccountingYear = null;
    }
  }

  public createCompanyBrowser(
    columnSelection: string[] = DEFAULT_SELECTED_COLUMNS,
  ): Observable<CompanyBrowserController> {
    const languageCode = 'en'; // TODO: Fetch from settings.
    const fileType = TabularFileFormat.Excel;
    return forkJoin([
      this._appService.identityDataCache.get().pipe(first()),
      this._companyFlagService.companyFlagTypeUuidToAppFeatureKeyDict().pipe(first()),
      this._membershipGeoCountryService.geoCountryListByMembershipCache.get().pipe(first()),
    ]).pipe(
      map((payload) => {
        const [identity, flagDict, membershipGeoCountryFocus] = payload;
        return new CompanyBrowserController(
          identity.app_customer.uuid,
          membershipGeoCountryFocus,
          languageCode,
          fileType,
          this,
          flagDict,
          columnSelection,
        );
      }),
    );
  }

  public createCompanyBrowserFromParams(
    params: ParamMap,
    columnFieldKeys: string[] = DEFAULT_SELECTED_COLUMNS,
    defaultFilters: CompanyBrowserFilter[],
    defaultOrdering: CompanyBrowserOrdering[],
  ): Observable<CompanyBrowserController> {
    let unused = true;
    if (params.has('selected_entities')) {
      columnFieldKeys = params
        .get('selected_entities')
        .split(',')
        .map((abbrKey) => fromAbbr(abbrKey));
      unused = false;
    }

    let orderings: CompanyBrowserOrdering[] = [];
    if (defaultOrdering && defaultOrdering.length > 0) {
      orderings.push(...defaultOrdering);
    } else if (params.has('order_by')) {
      orderings = params
        .get('order_by')
        .split(',')
        .map((orderingString) => CompanyBrowserOrdering.createFromQueryString(orderingString));
      unused = false;
    }

    let filters: CompanyBrowserFilter[] = [];
    if (defaultFilters && defaultFilters.length > 0) {
      filters = defaultFilters;
      unused = false;
    }
    for (const key of params.keys) {
      const values = params.getAll(key);
      for (const value of values) {
        if (fromAbbr(key) === 'company_nace_code_aggregate.nace_codes') {
          // Handling legacy encodings
          const naceCodeMask = this.naceCodesToBase62Mask(value.split(':')[1].split(','));
          const naceFilter = new CompanyBrowserFilter(
            CompanyBrowserFilterKey.NaceCodeMask,
            OPERATORS.Equals,
            naceCodeMask,
          );
          filters.push(naceFilter);
          unused = false;
        } else if (includes(key, '.') && includes(value, ':')) {
          filters.push(CompanyBrowserFilter.createFromQueryString(fromAbbr(key), value));
          unused = false;
        }
      }
    }

    const defaultLanguageCode = DEFAULT_LANGUAGE_CODE;
    const defaultFileType = TabularFileFormat.Excel;

    return forkJoin([
      this._appService.identityDataCache.get().pipe(first()),
      this._companyFlagService.companyFlagTypeUuidToAppFeatureKeyDict().pipe(first()),
      this._membershipGeoCountryService.geoCountryListByMembershipCache.get().pipe(first()),
    ]).pipe(
      map((payload) => {
        const [identity, flagDict, membershipGeoCountryFocus] = payload;
        return new CompanyBrowserController(
          identity.app_customer.uuid,
          membershipGeoCountryFocus,
          params.get('language_code') || defaultLanguageCode,
          (params.get('file_type') as TabularFileFormat) || defaultFileType,
          this,
          flagDict,
          columnFieldKeys,
          filters,
          orderings,
          unused,
          params.has('filter_accounting_year') ? parseInt(params.get('filter_accounting_year')) : null,
          params.has('from_accounting_year') ? parseInt(params.get('from_accounting_year')) : null,
          params.has('to_accounting_year') ? parseInt(params.get('to_accounting_year')) : null,
          params.has('desc_accounting_years_flag') ? parseInt(params.get('desc_accounting_years_flag')) > 0 : null,
        );
      }),
    );
  }

  public postCompanyBrowserTransientFile(params: HttpParams): Observable<TransientFileMetaEntity> {
    const partialTransientFile = {
      filename: 'company-browser-export',
    };
    return this._applicationApiAccess.post<TransientFileMetaEntity>(
      'company-browser-result/transient-file-meta',
      partialTransientFile,
      { params: params },
      [
        AppFeatureKey.base,
        ...COMPANY_FLAG_FEATURES,
        AppFeatureKey.collateral,
        AppFeatureKey.company_browser_unrestricted,
        AppFeatureKey.company_risk_score,
        AppFeatureKey.norwegian_property_data,
        AppFeatureKey.events_news,
        AppFeatureKey.events_news_digital,
        AppFeatureKey.events_news_print,
        AppFeatureKey.company_browser_media_mention_search,
        ...COUNTRY_FEATURES,
      ],
      [AppRoleKey.norwegian_property_data, ...COMPANY_FLAG_ROLES],
    );
  }

  public postCompanyBatchFromCompanyBrowserResult(
    params: HttpParams,
    appCustomerUuid: string,
  ): Observable<CompanyBatchEntity> {
    return this._applicationApiAccess.post<CompanyBatchEntity>(
      'company-browser-result/company-batch',
      {
        app_customer_uuid: appCustomerUuid,
      },
      { params },
    );
  }

  public fetchCompanyBrowserResult(params: HttpParams): Observable<SimpleCollection<CompanyBrowserCompositeEntity>> {
    return this._applicationApiAccess
      .get<SimpleCollection<CompanyBrowserCompositeEntity>>(
        '/company-browser-result',
        {
          params: params,
        },
        [
          AppFeatureKey.base,
          AppFeatureKey.collateral,
          AppFeatureKey.company_browser_unrestricted,
          AppFeatureKey.company_risk_score,
          AppFeatureKey.historical_ownership,
          AppFeatureKey.norwegian_property_data,
          AppFeatureKey.events_news,
          AppFeatureKey.events_news_digital,
          AppFeatureKey.events_news_print,
          AppFeatureKey.company_browser_media_mention_search,
          ...COMPANY_FLAG_FEATURES,
          ...COUNTRY_FEATURES,
        ],
        [AppRoleKey.norwegian_property_data, ...COMPANY_FLAG_ROLES],
      )
      .pipe(
        catchError((error: unknown) => {
          if (isNkHttpError(error) && error.error.error_key === 'company_browser.invalid_mention_search') {
            const toastrText = this._translateService.instant(
              extractTranslation('company_browser.mention_search.query_error_toastr_text'),
            ) as string;
            const toastrTitle = this._translateService.instant(
              extractTranslation('company_browser.mention_search.query_error_toastr_title'),
            ) as string;
            this._toastr.error(toastrText, toastrTitle, {
              closeButton: true,
              tapToDismiss: true,
              timeOut: 10000,
            });
          }
          return of<SimpleCollection<CompanyBrowserCompositeEntity>>({
            count: 0,
            elements: [],
            has_more: false,
          });
        }),
      );
  }

  public fetchExtendedCompanyBrowserCount(params: HttpParams): Observable<number> {
    return this.initialDataLoading.pipe(
      startWith(null),
      first(),
      switchMap(() => {
        params = params.append('only_count_flag', String(true));
        return this.fetchCompanyBrowserResult(params);
      }),
      map((collection) => {
        return collection.count;
      }),
    );
  }

  public fetchExtendedCompanyBrowserResult(params: HttpParams): Observable<CompanyBrowserExtendedCompositeCollection> {
    const start = performance.now();
    params = params.append('only_data_flag', String(true));

    return this.initialDataLoading.pipe(
      filter((loading) => !loading),
      first(),
      switchMap(() => {
        return this.fetchCompanyBrowserResult(params).pipe(
          switchMap((collection) => {
            const geoPostalCodeUuids = collection.elements.reduce((res: string[], i) => {
              if (i.company_location) {
                res = res.concat(i.company_location.geo_postal_code_uuid);
              }
              return res;
            }, []);
            return combineLatest([
              geoPostalCodeUuids.length
                ? this._geoService.geoPostalCodesByUuidsCache.get('null', geoPostalCodeUuids)
                : of([]),
            ]).pipe(
              map(([geoPostalCodes]) => {
                const duration = performance.now() - start;
                const geoPostalCodesDict = arrayToDict(geoPostalCodes, (cc) => cc.uuid);
                return {
                  elements: collection.elements.map((element) => {
                    return {
                      ...element,
                      company_flag_types: element.company_flag_history_aggregate
                        ? element.company_flag_history_aggregate.company_flag_type_uuids.map(
                            (companyFlagTypeUuid) => this._companyFlagTypeDict[companyFlagTypeUuid],
                          )
                        : null,
                      nace_codes: element.company_nace_code_aggregate
                        ? element.company_nace_code_aggregate.nace_code_uuids.map(
                            (naceCodeUuid) => this._naceCodeDict[naceCodeUuid],
                          )
                        : null,
                      municipality: element.company_location
                        ? this._municipalityDict[element.company_location.geo_municipality_uuid]
                        : null,
                      subdivision: element.company_location
                        ? this._subdivisionDict[element.company_location.geo_subdivision_uuid]
                        : null,
                      postal_code: element.company_location
                        ? geoPostalCodesDict[element.company_location.geo_postal_code_uuid]
                        : null,
                      geo_country: element.company_location
                        ? this._geoCountryDict[element.company_location.geo_country_uuid]
                        : null,
                      collateral_types: element.company_collateral_aggregate
                        ? element.company_collateral_aggregate.company_collateral_type_uuids.map(
                            (companyTypeUuid) => this._collateralTypeDict[companyTypeUuid],
                          )
                        : null,
                      collateral_creditors: element.company_collateral_aggregate?.company_collateral_creditors,
                    };
                  }),
                  count: null,
                  has_more: collection.has_more,
                  query: params.toString(),
                  query_duration: duration,
                  query_success: true,
                };
              }),
            );
          }),
        );
      }),
    );
  }

  public naceCodesToBase62Mask(naceCodeStrings: string[]): string {
    const allNaceCodesByCodeDict = arrayToDict(this._naceCodes, (nc) => nc.nace_code);
    const naceCodeIds = arrayDistinct(naceCodeStrings.map((ncs) => allNaceCodesByCodeDict[ncs].nace_code_id));
    return encodeIdsToBaseNMask(naceCodeIds);
  }

  public base62MaskToNaceCodes(base62Mask: string): NaceCodeEntity[] {
    return CompanyBrowserService._base62MaskToNaceCodes(this._allNaceCodesByIdDict, base62Mask);
  }

  public findAllChildrenOfNaceCode(parentsNaceCodeId: number): NaceCodeEntity[] {
    // TODO: String is using nace.nace_code. Make it more clear or switch to nace code entity
    const allChildren: NaceCodeEntity[] = [];
    const parent = this._naceCodes.find((a) => a.nace_code_id === parentsNaceCodeId);
    const maxNaceCodeLevel = 5;
    if (parent) {
      const unhandled = [parent];
      while (unhandled.length > 0) {
        const currentParent = unhandled.pop();
        const currentChildren = this._naceCodes.filter((a) => a.parent_nace_code_uuid === currentParent.uuid);
        allChildren.push(...currentChildren);
        if (currentChildren.length > 0 && currentChildren[0].hierarchy_level < maxNaceCodeLevel) {
          unhandled.push(...currentChildren);
        }
      }
      return allChildren;
    } else {
      console.warn('Code error: Could not find nace code when trying to find children');
    }
  }

  public findTopLevelNaceCodeNestedList(naceCodesChecked: NaceCodeEntity[]): {
    checkedItems: NaceCodeEntity[];
    indeterminateItems: NaceCodeEntity[];
  } {
    /*
    Turns a set of nested checked items into a list where children are not delivered if parent is checked.
    Gives highest possible level.
    1. find all top level selected, save them.
    2. from the ones that are not in first set, check their children. Repeat.

    Example:
    [01.460, 36.000, 37.000, 38.110, 38.120, 38.210, 38.220, 38.310, 38.320, 39.000, 36, 37, 38, 39, E]
    Returns [E, 01.460]
    */

    const naceCodes = this._naceCodes;
    if (!naceCodesChecked || naceCodesChecked.length === 0) {
      return null;
    }

    const naceCodesCheckedDict = naceCodesChecked.reduce((res: { [naceCodeUuid: string]: NaceCodeEntity }, i) => {
      res[i.uuid] = i;
      return res;
    }, {});

    let checkedItems: NaceCodeEntity[] = [];
    let indeterminateItems: NaceCodeEntity[] = [];
    // Starts out with the first level beeing unresolved.
    let toResolve = naceCodes.filter((a) => a.hierarchy_level === this._naceCodesFirstLevel);

    for (let level = this._naceCodesFirstLevel; level <= this._naceCodesLastLevel; level++) {
      const unresolvedForLevel = toResolve.filter((a) => a.hierarchy_level === level);
      const { unresolved, checked, indeterminate } = this.findCheckedNaceCodeForLevel(
        unresolvedForLevel,
        naceCodesCheckedDict,
        this._naceCodesParentDict,
      );
      toResolve = unresolved;
      checkedItems = [...checkedItems, ...checked];
      indeterminateItems = [...indeterminateItems, ...indeterminate];
    }
    return { checkedItems, indeterminateItems };
  }

  public findCheckedNaceCodeForLevel(
    unresolvedForLevel: NaceCodeEntity[],
    naceCodesCheckedDict: { [naceCodeUuid: string]: NaceCodeEntity },
    naceCodesParentDict: { [naceCodeUuid: string]: NaceCodeEntity[] },
  ): {
    unresolved: NaceCodeEntity[];
    checked: NaceCodeEntity[];
    indeterminate: NaceCodeEntity[];
  } {
    let unresolved: NaceCodeEntity[] = []; // children might be checked.
    const checked = [];
    const indeterminate = [];
    for (const nace of unresolvedForLevel) {
      if (naceCodesCheckedDict[nace.uuid]) {
        checked.push(nace);
      } else {
        // if we found an unchecked item, sett all children to unresolved.
        const children = naceCodesParentDict[nace.uuid] || [];
        const isIndeterminate = this.isParentIndeterminate(children, naceCodesCheckedDict);
        if (isIndeterminate) {
          indeterminate.push(nace);
        }
        unresolved = [...unresolved, ...children];
      }
    }
    return { unresolved, checked, indeterminate };
  }
  public isParentIndeterminate(
    children: NaceCodeEntity[],
    naceCodesCheckedDict: { [naceCodeUuid: string]: NaceCodeEntity },
  ): boolean {
    const checkedChildren = children.filter((nace) => naceCodesCheckedDict[nace.uuid]);
    return checkedChildren.length !== children.length;
  }
}
